/**
 * O VOCABULÁRIO COMUM DA E2 — o espelho de `backend/app/core/vocabulario.py`.
 *
 * É a §4 de docs/compliance/E2-CONTRATOS.md: a grafia única de cada enumeração
 * que o backend grava e a tela lê. Oito pacotes publicam e consomem os mesmos
 * estados, fontes e rótulos; duas cópias do mesmo enum divergem no dia em que
 * alguém ajustar uma só, e a divergência é invisível — a tela continua
 * desenhando um rótulo plausível para uma palavra que o backend não grava mais.
 *
 * AS REGRAS:
 * - nenhum componente redeclara estes valores: importa daqui (os rótulos e as
 *   cores de cada tela ficam nos `vocabulario-<assunto>.ts`, que importam os
 *   enums deste arquivo);
 * - valor novo não se inventa numa tela: o coordenador edita o contrato, o
 *   `vocabulario.py` e este arquivo no mesmo commit;
 * - cada literal abaixo é JSON válido (aspas duplas, sem vírgula sobrando, sem
 *   comentário dentro do literal): `backend/app/core/test_vocabulario.py` lê
 *   este arquivo no host e compara constante a constante com o Python. O tipo
 *   de cada enum sai de `(typeof X)[number]`.
 */

export const VERSAO = "vocabulario-e2/1" as const;

// --- etapas e estados de linha
// A ordem canônica do grafo (§3.2): é também o desempate do agendador.
// "pare" (07/10/2026): o PARE medido no vídeo da frontal, sem IA; opcional, como a telemetria.
export const NOS = [
  "audio", "cameras", "fala", "seta", "visao:interna", "visao:frontal", "visao:traseira",
  "visao:lateral", "insumo", "telemetria", "pare", "falas", "motor", "conduta", "fusao", "manobras",
  "veredito", "compliance", "parecer", "limpeza"
] as const;
// NOS sem telemetria, pare, limpeza, compliance e parecer: = grafo.obrigatoria = compliance.ETAPAS_EXIGIDAS.
export const NOS_OBRIGATORIOS = [
  "audio", "cameras", "fala", "seta", "visao:interna", "visao:frontal", "visao:traseira",
  "visao:lateral", "insumo", "falas", "motor", "conduta", "fusao", "manobras", "veredito"
] as const;
export const PAPEIS_CAMERA = ["interna", "frontal", "lateral", "traseira"] as const;
export const EM_CURSO = ["pendente", "processando"] as const;
export const ESTADOS_BASE = ["pendente", "processando", "pronto", "erro", "interrompido"] as const;
// "audio_reprovado" (06/10/2026): o áudio parado pela régua do tempo de ruído, antes do ASR.
export const RECUSAS_TERMINAIS = [
  "sem_audio", "nao_compativel", "bloqueado", "nao_solicitada", "audio_reprovado"
] as const;
// Só na API: linha que não existe é `ausente` (200, nunca 404).
export const AUSENTE = "ausente" as const;

// --- orquestrador
export const ESTADOS_DE_NO = [
  "PENDENTE", "AGUARDANDO_DEPENDENCIA", "PRONTA", "EM_EXECUCAO", "CONCLUIDA", "REUTILIZADA",
  "NAO_SOLICITADA", "IGNORADA", "PRECISA_DE_DADO", "ERRO", "INTERROMPIDA", "CANCELADA",
  "DESATUALIZADA"
] as const;
// IGNORADA só satisfaz por escopo (§3.1): ver MOTIVOS_IGNORADA_QUE_SATISFAZEM.
export const NOS_SATISFEITOS = ["CONCLUIDA", "REUTILIZADA", "NAO_SOLICITADA", "IGNORADA"] as const;
// "audio_reprovado" (06/10/2026): o nó que depende do áudio reprovado pela régua do ruído.
export const MOTIVOS_IGNORADA_QUE_SATISFAZEM = ["nao_compativel", "sem_trilha", "audio_reprovado"] as const;
// Códigos de `Recusa`, NUNCA estado de linha (§2.0); a etapa pode ter os seus, em snake_case.
export const CODIGOS_DE_RECUSA = [
  "sem_exame", "sem_credencial_vertex", "sem_midia_local", "etapas_em_andamento", "sem_fontes",
  "mapa_ambiguo", "mapa_votos_insuficientes", "cameras_bloqueadas", "gatilho_sem_frontal"
] as const;
export const ESTADOS_DE_EXECUCAO = [
  "NA_FILA", "EM_EXECUCAO", "PRECISA_DE_DADO", "CONCLUIDA", "ERRO", "INTERROMPIDA", "CANCELADA",
  "CANCELANDO"
] as const;

// --- câmeras e mapa
export const MODOS_CAMERA = ["integral", "condicional", "nao_solicitada"] as const;
export const MODO_PADRAO_B = {
  "interna": "integral",
  "frontal": "integral",
  "lateral": "condicional",
  "traseira": "nao_solicitada"
} as const;
export const MODOS_POR_PAPEL = {
  "interna": ["integral"],
  "frontal": ["integral"],
  "lateral": ["integral", "condicional", "nao_solicitada"],
  "traseira": ["integral", "nao_solicitada"]
} as const;
// MAPA_ILEGIVEL (cadeia/1, 04/10/2026): a etapa 0 achou uma câmera sem leitura e o mapa não foi feito.
export const STATUS_MAPA = [
  "MAPA_COMPLETO", "MAPA_INCOMPLETO", "MAPA_INVALIDO", "MAPA_AMBIGUO", "ERRO_INFRA", "MAPA_ILEGIVEL"
] as const;
// null = sem defeito.
export const CODIGOS_DIAGNOSTICO_QUADRANTE = [
  "ilegivel", "escuro", "estourado", "liso", "lente_coberta"
] as const;
export const CONTEXTOS_PARADA = [
  "SEMAFORO", "PARE_OU_LINHA", "TRANSITO", "PEDESTRE", "BORDA_DA_VIA", "FINAL_DO_EXAME",
  "INDETERMINADO"
] as const;
export const LADOS_PARADA = ["DIREITA", "ESQUERDA", "CENTRO_DA_FAIXA", "INDETERMINADO"] as const;
export const SUBTIPOS_PARADA_LATERAL = [
  "CONTATO_VISIVEL_MEIO_FIO", "POSSIVEL_CONTATO_MEIO_FIO", "RODA_PROXIMA_MEIO_FIO",
  "APROXIMA_MEIO_FIO", "AFASTA_MEIO_FIO", "MEIO_FIO_VISIVEL", "NAO_OBSERVAVEL"
] as const;
export const IMPOSSIVEL_LATERAL = [
  "NAO", "OCLUSAO", "ESCURO", "CAMERA_DESLOCADA", "SEM_MEIO_FIO_NO_QUADRO"
] as const;
export const MOTIVOS_GATILHO_LATERAL = [
  "inicio", "ultima_parada", "sem_contexto", "volante", "cambio"
] as const;

// --- qualidade do insumo
export const ROTULOS_QUALIDADE_FONTE = ["BOA", "ACEITAVEL", "RUIM", "INOPERANTE"] as const;
// CRITICA (decisão 10) = INOPERANTE agregado; é rótulo de EXAME, nunca de fonte.
export const ROTULOS_QUALIDADE_EXAME = ["BOA", "ACEITAVEL", "RUIM", "CRITICA"] as const;
export const IDENTIFICACAO_CAMERA = ["IDENTIFICADA", "AMBIGUA", "NAO_IDENTIFICADA"] as const;
export const ROTULOS_TELEMETRIA = ["SEM_TELEMETRIA", "PARCIAL", "UTIL", "BOA"] as const;

// --- tempo
// As três classes da decisão 4. O instrumento vai em `fonte_do_instante`, nunca aqui.
export const ORIGEM_TEMPO = ["MEDIDO", "ESTIMADO", "SEM_SINCRONIA"] as const;
// Áudio, já gravado: não renomear.
export const ESTADO_DA_ANCORA = ["MEDIDA", "ESTIMADA", "NAO_MEDIDA"] as const;
export const ORIGEM_TEMPO_DA_ANCORA = {
  "MEDIDA": "MEDIDO",
  "ESTIMADA": "ESTIMADO",
  "NAO_MEDIDA": "SEM_SINCRONIA"
} as const;
// TELEMETRIA-PLANO §T4.
export const SINCRONIA_GPS = ["MEDIDO", "INTERPOLADO", "NAO_SINCRONIZADO"] as const;
export const ORIGEM_TEMPO_DA_SINCRONIA_GPS = {
  "MEDIDO": "MEDIDO",
  "INTERPOLADO": "ESTIMADO",
  "NAO_SINCRONIZADO": "SEM_SINCRONIA"
} as const;

// --- fontes
// = fusao.padroes.FONTES
export const FONTES_PRIMARIAS = [
  "audio", "seta", "interna", "frontal", "lateral", "traseira"
] as const;
export const PAPEL_DA_FONTE = ["PRIMARIA", "CORROBORATIVA", "CONTEXTUAL"] as const;
export const PAPEL_POR_FONTE = {
  "audio": "PRIMARIA",
  "seta": "PRIMARIA",
  "interna": "PRIMARIA",
  "frontal": "PRIMARIA",
  "lateral": "PRIMARIA",
  "traseira": "PRIMARIA",
  "telemetria": "CORROBORATIVA",
  "pare": "CORROBORATIVA",
  "fala": "CONTEXTUAL",
  "falas": "CONTEXTUAL",
  "conduta": "CONTEXTUAL",
  "motor": "CONTEXTUAL"
} as const;

// --- cegueira (princípio 7): o que a passada cega NUNCA lê
// `exams.ingest_payload_raw` é o payload INTEIRO: só ->'percurso' e ->'telemetrias' são
// permitidos. `veredito_processamento.n_itens_` é PREFIXO.
// As cinco `ensinamento*` (26/09) são o laudo do dono sobre a nossa IA (app/ensinamentos),
// tabelas inteiras; a v2 (tarde de 26/09) trouxe o backlog de execução e o batimento da
// curadora. `infracao_verificacoes` (27/09) é o IA OK do dono (app/infracoes/verificacao.py),
// tabela inteira. O comentário fica FORA do literal: `test_vocabulario.py` o lê com json.loads.
export const COLUNAS_DO_LAUDO = [
  "exams.resultado_exame", "exams.pontuacao_oficial", "exams.training_annotations",
  "exams.ingest_payload_raw", "exam_etapas", "veredito_processamento.verificacao",
  "veredito_processamento.n_itens_",
  "ensinamentos", "ensinamento_vinculos", "ensinamento_eventos", "ensinamento_pendencias",
  "ensinamento_curadora", "infracao_verificacoes"
] as const;

// --- compliance
// A ordem É a SEVERIDADE; o último é exclusivo e exige cobertura completa.
export const ESTADOS_COMPLIANCE = [
  "FALHA_TECNICA", "POSSIVEL_IRREGULARIDADE", "ALERTA_DE_SEGURANCA",
  "ALERTA_DE_INFRACAO_OBSERVAVEL", "ALERTA_COMPORTAMENTAL", "BAIXA_OBSERVABILIDADE",
  "SEM_ALERTAS_RELEVANTES"
] as const;
export const TIPOS_DE_ALERTA = [
  "FALHA_TECNICA", "POSSIVEL_IRREGULARIDADE", "ALERTA_DE_SEGURANCA",
  "ALERTA_DE_INFRACAO_OBSERVAVEL", "ALERTA_COMPORTAMENTAL", "BAIXA_OBSERVABILIDADE"
] as const;
export const ABRANGENCIAS = ["exame", "trecho"] as const;
export const CLASSES_DE_INFRACAO = ["A", "B", "NO"] as const;
// "candidato" (17/10/2026): `candidato.conduta_atipica`, o alerta da oferta ao examinador.
export const PREFIXOS_DE_FAMILIA = [
  "execucao", "insumo", "cobertura", "conduta", "falas", "motor", "seguranca", "anexo_k", "seta",
  "infracao", "veiculo", "candidato"
] as const;
export const ANEXO_K_RESPOSTAS = ["SIM", "NAO", "ATENCAO", "NAO_AVALIAVEL"] as const;
export const PASSADAS = ["cega", "pos_cega"] as const;
export const DESTINOS_TRIAGEM = [
  "SUPERVISOR", "RECUPERACAO_TECNICA", "SEM_ALERTA_EM_SOMBRA"
] as const;
// Os dois eixos da triagem (AD-5, opção "a", decidida pelo dono em 24/09).
export const EIXOS_DA_TRIAGEM = ["resultado", "conduta"] as const;
// Os blocos de 20 s da fusão por período (20_000 no Python).
export const BLOCO_MS = 20000;
// ref = "<tipo>:<id>"; falta = "falta:<ocasiao_id>:<id_regra>";
// janela = "janela:<etapa>:<papel>:<indice>"
export const TIPOS_DE_EVIDENCIA = [
  "visao_evento", "fala", "som", "episodio_seta", "manobra", "falta", "conduta", "motor",
  "telemetria", "janela", "anexo_k", "bipe"
] as const;

// --- seta
export const ROTULOS_SETA = [
  "SETA_PRESENTE", "POSSIVEL_OMISSAO_SETA_REVISAR", "SETA_NAO_OBSERVAVEL"
] as const;
export const RELEVANCIAS_SETA = ["OMISSAO", "SO_PRESENCA"] as const;
export const FONTES_DO_INSTANTE_DA_SETA = ["rele", "escuta", "esteira_ancorada"] as const;
export const ESTADOS_PODER_LOCAL = ["MEDIDO", "NAO_MEDIDO", "NAO_CALCULADO"] as const;
export const TRECHOS_PODER_LOCAL = ["INFORMATIVO", "NAO_INFORMATIVO"] as const;
// Mais os prefixados "audio_<rotulo>" e "etapa_seta_<estado>".
// "janela_truncada_no_inicio" (AD-9, C2.7): menos de 2/3 do trecho antes do início no vídeo.
// "ruido_no_canal_da_seta" (06/10/2026): as ausências do canal da seta anuladas pelo tempo de
// ruído — antes de "parada_final_contexto_nao_elegivel", na ordem do backend.
// "parada_final_contexto_nao_elegivel" (C1.10): a última PARADA que não é parada final,
// declarada sem ler a seta — antes do último, na ordem do backend.
// "seta_ligada_continuamente" (E2-bis): o relé soou a manobra inteira; é leitura, não falta.
export const MOTIVOS_SETA_NAO_OBSERVAVEL = [
  "rele_nao_mensuravel", "audio_sem_ficha", "trecho_sem_cobertura", "mascaramento_local",
  "indicio_sem_medida", "fontes_mudaram", "janela_truncada_no_inicio", "ruido_no_canal_da_seta",
  "parada_final_contexto_nao_elegivel", "seta_ligada_continuamente"
] as const;

// --- falas
export const CLASSIFICACOES_FALA = [
  "COMANDO_DE_MANOBRA", "ORIENTACAO", "ADVERTENCIA", "CORRECAO", "ORDEM_DE_PARADA",
  "ORDEM_LIGAR_DESLIGAR", "INTERRUPCAO_DA_PROVA", "RESULTADO_ANTECIPADO", "CONVERSA_ALHEIA",
  "RESPOSTA_DO_CANDIDATO", "INAUDIVEL", "OUTRO"
] as const;
export const ALVOS_FALA = [
  "SETA", "MOTOR", "PARADA", "BALIZA", "CONVERSAO", "VELOCIDADE", "CINTO", "FAIXA", "SINALIZACAO",
  "PEDESTRE", "RE", "VEICULO", "PROCEDIMENTO", "NENHUM"
] as const;
export const MOTOR_NA_FALA = ["PARTIDA", "ESTOL_DECLARADO", "ORDEM_DESLIGAR"] as const;
export const ESTADOS_DE_ITEM_FALA = ["CLASSIFICADA", "SEM_RESPOSTA"] as const;

// --- conduta (nomes do legado conduta_v2 1.1, mais informa_falta; sem ia_conduta — decisão K)
export const CLASSIFICACOES_CONDUTA = [
  "NORMAL", "INCONCLUSIVA", "ATIPICA", "POSSIVEL_INCONFORMIDADE"
] as const;
export const RESULTADOS_CONDUTA = [
  "CONDUTA SEM ACHADOS RELEVANTES", "CONDUTA COM EVENTOS QUE MERECEM REVISÃO",
  "ANÁLISE INCONCLUSIVA"
] as const;
// = todo `regra=` que conduta_v2.py atribui sem IA (grep, 23/09).
// "oferta_ao_examinador" (17/10/2026): a camada do candidato (falante INCERTO, ator_alvo candidato).
export const REGRAS_CONDUTA = [
  "base", "contradicao", "substituicao", "resultado_antecipado", "informa_falta", "sobreposicao",
  "tom_elevado", "visual", "gesto", "olhar", "interacao_com_condutor", "movimento_para_controle",
  "rotulo_recusado", "distracao_prolongada", "objeto_com_qualificador", "correlacao",
  "intervencao_fisica_possivel", "oferta_ao_examinador"
] as const;
export const CATEGORIAS_CONDUTA = [
  "COMANDO", "INTERVENCAO_VERBAL", "INTERVENCAO_GESTUAL", "INTERVENCAO_FISICA", "ATENCAO",
  "DISTRACAO", "COMUNICACAO", "ORIENTACAO", "COMPORTAMENTO_ATIPICO", "OUTRO"
] as const;
// O CHECK da fonte: gesto e intervenção física não saem de áudio.
export const CATEGORIAS_SO_COM_IMAGEM = ["INTERVENCAO_GESTUAL", "INTERVENCAO_FISICA"] as const;
export const QUALIFICADORES_CONDUTA = ["objeto", "atencao", "interacao", "repeticao"] as const;
export const FALANTES_CONDUTA = ["EXAMINADOR", "INCERTO"] as const;

// --- motor
export const TIPOS_MOTOR = ["PARTIDA", "RELIGOU", "MOTOR_APAGOU"] as const;
export const SUBTIPOS_MOTOR = {
  "PARTIDA": ["INICIAL", "DURANTE_O_EXAME"],
  "RELIGOU": ["INDICADOR_DE_MORTE", "COMANDADO", "DIRECAO_INDETERMINADA"],
  "MOTOR_APAGOU": ["PAREADO", "RESGATE"]
} as const;
export const CAMADAS_MOTOR = ["CORROBORADO", "INDICADOR", "SEM_CORROBORACAO"] as const;
export const RESULTADOS_CORROBORADOR = [
  "SIM", "NAO", "NAO_OBSERVAVEL", "NAO_AVALIADO", "ERRO"
] as const;
export const MOTOR_NO_INICIO = ["LIGADO", "CALADO", "INDECISO"] as const;
export const CONDICOES_CERTEZA_MOTOR = [
  "evidencia_positiva", "duas_testemunhas", "refutador_consultado", "sem_borda_de_janela",
  "placebo", "exclusao_de_contexto", "confianca_fora_do_criterio"
] as const;

// --- telemetria
export const PROCEDENCIAS_TELEMETRIA = ["GPS", "SENSOR", "DERIVADO_GEOESPACIAL"] as const;
export const POSICOES_NO_PERCURSO = [
  "UNICO", "TRECHO_PARADO", "NO_ULTIMO_PONTO", "NO_PRIMEIRO_PONTO", "SEM_PONTO"
] as const;
export const RELACOES_CORROBORATIVAS = ["CONCORDA", "DIVERGE", "INDETERMINADO", "PROXIMO"] as const;
